import { HttpService } from "../../../utils/DesigningHttpService.js";
import { agencySessionKey, canWriteAgencyCommerce, getAgencySession, isCompanySalesAgent } from "../../../utils/agencySession.ts";
import { readStore } from "../storefrontService.ts";
import { AccountOrder, readAccountOrder } from "./accountOrderService.ts";
import { t } from "../../../features/store-language/i18n.ts";

type OrderWrite = {
  orderId?: number;
  body: {
    salesAgencyId?: number;
    requestId?: string;
    version?: string;
    title?: string | null;
    description?: string | null;
    items: { id: number | null; productVariantId: number; quantity: number }[];
  };
};

export type CatalogOrderOption = Pick<AccountOrder, "id" | "title" | "orderNumber" | "agencyTitle">;

const http = new HttpService();
let destination: { session: string; id?: number; title?: string; salesAgencyId?: number; order?: AccountOrder } | undefined;

export async function readCatalogDestination(): Promise<AccountOrder | null> {
  const session = agencySessionKey();
  const id = selectedCatalogOrder();
  if (!id) return null;
  try {
    const order = await readAccountOrder(id);
    if (session !== agencySessionKey() || id !== selectedCatalogOrder()) return null;
    if (order.status !== "Draft") {
      resetCatalogOrder();
      return null;
    }
    destination = { session: session!, id, order };
    return order;
  } catch (error) {
    if (session === agencySessionKey() && id === selectedCatalogOrder()) {
      if (destination) destination.order = undefined;
      if ((error as { response?: { status?: number } }).response?.status === 404) resetCatalogOrder();
    }
    throw error;
  }
}

function storageKey() {
  const session = getAgencySession();
  return `catalog-order-request:${window.location.host}:${session?.userId}:${session?.salesAgencyId}:${session?.membershipId}`;
}

function requireSession(session: string | null) {
  if (!session || session !== agencySessionKey() || !canWriteAgencyCommerce()) {
    throw new Error(t("اجازهٔ تغییر سفارش را ندارید."));
  }
}

export function selectedCatalogOrder(): number | undefined {
  return destination?.session === agencySessionKey() ? destination.id : undefined;
}

export function selectedCatalogOrderTitle(): string | undefined {
  return destination?.session === agencySessionKey() ? destination.title : undefined;
}

export function selectedCatalogAgency(): number | undefined {
  return destination?.session === agencySessionKey() ? destination.salesAgencyId : undefined;
}

// Selecting a destination must not add a product or create an empty server order.
export async function selectCatalogDestination(orderId?: number, title?: string, salesAgencyId?: number): Promise<AccountOrder | null> {
  const session = agencySessionKey();
  requireSession(session);
  if (hasPendingCatalogOrder()) {
    throw new Error(t("نتیجهٔ درخواست قبلی مشخص نیست؛ تلاش مجدد همان درخواست را بررسی می‌کند."));
  }
  if (orderId) {
    const order = await readAccountOrder(orderId);
    requireSession(session);
    if (order.status !== "Draft") {
      throw new Error(t("فقط سفارش فعالِ پیش‌نویس قابل ویرایش است."));
    }
    destination = { session: session!, id: order.id, order };
    return order;
  }
  if (!title?.trim() || title.trim().length > 200) {
    throw new Error(t("عنوان سفارش را وارد کنید (حداکثر ۲۰۰ نویسه)."));
  }
  if (isCompanySalesAgent() && !salesAgencyId) throw new Error(t("نمایندگی را انتخاب کنید."));
  destination = { session: session!, title: title.trim(), salesAgencyId };
  return null;
}

export function resetCatalogOrder() {
  destination = undefined;
}

export function hasPendingCatalogOrder(): boolean {
  return Boolean(sessionStorage.getItem(storageKey()));
}

export function pendingCatalogOrderId(): number | undefined {
  const saved = sessionStorage.getItem(storageKey());
  return saved ? (JSON.parse(saved) as OrderWrite).orderId : undefined;
}

export async function latestCatalogDraft(): Promise<CatalogOrderOption | null> {
  const result = await readStore("orders/latest-draft");
  return result.order;
}

export async function catalogDraftOptions(): Promise<CatalogOrderOption[]> {
  const session = agencySessionKey();
  const drafts: CatalogOrderOption[] = [];
  let count = 0;
  for (let page = 1; ; page++) {
    const result: { items: AccountOrder[]; total: number } = await readStore(`orders?page=${page}&pageSize=100`);
    requireSession(session);
    drafts.push(...result.items.filter(function (order) { return order.status === "Draft"; }));
    count += result.items.length;
    if (!result.items.length || count >= result.total) return drafts;
  }
}

export async function writeCatalogOrder(variantId: number, quantity: number, orderId?: number, title?: string, salesAgencyId?: number): Promise<AccountOrder | null> {
  const session = agencySessionKey();
  requireSession(session);
  const key = storageKey();
  const saved = sessionStorage.getItem(key);
  let request: OrderWrite;
  if (saved) {
    // Explicit retry always replays the exact operation, never the latest click.
    request = JSON.parse(saved);
  } else if (orderId) {
    // Reuse the last server-confirmed snapshot; its version still guards the PUT.
    const snapshot = destination?.session === session && destination.id === orderId
      ? destination.order : undefined;
    let order: AccountOrder;
    try { order = snapshot || await readAccountOrder(orderId); }
    catch (error) {
      if (session === agencySessionKey() && orderId === selectedCatalogOrder()
        && (error as { response?: { status?: number } }).response?.status === 404) resetCatalogOrder();
      throw error;
    }
    requireSession(session);
    if (order.status !== "Draft") {
      resetCatalogOrder();
      throw new Error(t("فقط سفارش فعالِ پیش‌نویس قابل ویرایش است."));
    }
    const items = order.items.map(function (line) {
      return { id: line.id as number | null, productVariantId: line.productVariantId, quantity: line.quantity };
    });
    const existing = items.find(function (line) { return line.productVariantId === variantId; });
    if (!Number.isInteger(quantity) || !quantity) throw new Error(t("تعداد نامعتبر است."));
    if (existing) {
      existing.quantity += quantity;
      if (existing.quantity < 0) throw new Error(t("تعداد سفارش تغییر کرده است؛ سفارش را دوباره بررسی کنید."));
      if (existing.quantity === 0) items.splice(items.indexOf(existing), 1);
    } else {
      if (quantity < 1) throw new Error(t("تعداد سفارش تغییر کرده است؛ سفارش را دوباره بررسی کنید."));
      items.push({ id: null, productVariantId: variantId, quantity });
    }
    request = { orderId, body: { version: order.version, title: order.title, description: order.description, items } };
  } else {
    if (!title?.trim() || title.trim().length > 200) throw new Error(t("عنوان سفارش را وارد کنید (حداکثر ۲۰۰ نویسه)."));
    if (isCompanySalesAgent() && !salesAgencyId) throw new Error(t("نمایندگی را انتخاب کنید."));
    request = { body: { salesAgencyId, requestId: crypto.randomUUID(), title: title.trim(), items: [{ id: null, productVariantId: variantId, quantity }] } };
  }
  requireSession(session);
  if (request.body.items.length > 500 || request.body.items.some(function (line) {
    return !Number.isInteger(line.quantity) || line.quantity < 1 || line.quantity > 100000;
  })) throw new Error(t("سفارش باید بین ۱ تا ۵۰۰ قلم با تعداد صحیح و معتبر داشته باشد."));
  // If storage is unavailable, fail before sending a non-idempotent request.
  sessionStorage.setItem(key, JSON.stringify(request));
  try {
    let id = request.orderId;
    let updatedOrder: AccountOrder | null = null;
    if (id) updatedOrder = await http.putForData(`/catalog/orders/${id}`, request.body);
    else {
      const response = await http.postasync("/catalog/orders", request.body);
      id = response.data.orderId;
    }
    sessionStorage.removeItem(key);
    requireSession(session);
    destination = { session: session!, id: id!, order: updatedOrder || undefined };
    return updatedOrder;
  } catch (error) {
    const status = (error as { response?: { status?: number } }).response?.status;
    if (status === 400 || status === 404 || status === 409) {
      sessionStorage.removeItem(key);
      if (session === agencySessionKey() && request.orderId === selectedCatalogOrder()) {
        if (status === 404) resetCatalogOrder();
        else if (destination) destination.order = undefined;
      }
    }
    throw error;
  }
}
